import { c as _c } from "react/compiler-runtime";
import { Lock, WarningCircleSolid, WarningSolid } from '@byway/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type DateValue, parseDate } from '@internationalized/date';
import clsx from 'clsx';
// Deliberately next/navigation, NOT ~/i18n/navigation: `pathname` is fed to
// window.history.replaceState below, which needs the REAL browser path. next-intl's usePathname is
// locale-stripped, so it would rewrite /en-US/map/build to /map/build and drop the locale from the
// URL bar. The `.includes('/map/build')` check above is prefix-safe either way.
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Heading } from 'ui';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { Stack } from '~/components/Layout/Stack';
import { RangeCalendar } from '~/components/RangeCalendar';
import { Slider } from '~/components/Slider';
import { Tab, TabContent, TabsContainer, TabsList } from '~/components/Tabs';
import { useToasts } from '~/components/Toast';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundContextParams, usePlaygroundDatesTab, usePlaygroundNights, usePlaygroundPreferences } from '~/stores/playgroundStore';
import { CHOSEN_DEPARTURE_DATE } from '~/types/Date.types';
import type { PlaygroundContext } from '~/types/Playground.types';
import { logError, trackEvent } from '~/utils';
import { getLowSeasonDefaultStartDate } from '~/utils/dateHelpers/dateHelpers';
import { getTrackingCategory } from '~/utils/tracking';
import styles from './FlexibleDatesPanel.module.css';
const MIN_NIGHTS = 2;
interface FlexibleDatesPanelProps {
  layout?: 'horizontal' | 'vertical';
}
const FlexibleDatesPanel = ({
  layout = 'vertical'
}: FlexibleDatesPanelProps = {}) => {
  const t = useTranslations();
  const contextParams = usePlaygroundContextParams();
  const preferences = usePlaygroundPreferences();
  const playgroundContext = usePlaygroundContext();
  const {
    closeDrawer,
    setContext,
    setEndDate,
    setNights,
    setStartDate,
    setDatesTab
  } = usePlaygroundActions();
  const toasts = useToasts();
  const addDepartureAndReturnDates = api.playground.addDepartureAndReturnDates.useMutation();
  const isLoading = addDepartureAndReturnDates.isLoading;
  const [isCalendarTouched, setIsCalendarTouched] = useState(false);
  const [calendarWarning, setCalendarWarning] = useState<string[] | null>(null);
  const [nightsWarning, setNightsWarning] = useState<string[] | null>(null);
  const datesTab = usePlaygroundDatesTab();
  const nights = usePlaygroundNights();
  const {
    minimumDuration
  } = usePlaygroundContextParams();
  const pathname = usePathname();
  const isPlaygroundBuildPage = pathname?.includes('/map/build');
  const defaultDatepickerValues = datesTab === 'calendar' && (isPlaygroundBuildPage ? contextParams?.startDate && contextParams?.endDate : preferences?.startDate && preferences?.endDate) ? {
    start: parseDate(isPlaygroundBuildPage && contextParams?.startDate ? contextParams.startDate : preferences?.startDate!),
    end: parseDate(isPlaygroundBuildPage && contextParams?.endDate ? contextParams.endDate : preferences?.endDate!)
  } : undefined;

  // For map/build we derive nights from context; for other pages (e.g. blueprint-index) we should prefer the user's saved preferences so selections persist across openings.
  const defaultNightsValue = isPlaygroundBuildPage ? nights ?? preferences?.nights ?? null : preferences?.nights ?? nights ?? null;
  const DateValue: z.ZodType<DateValue> = z.any();
  const calendarSchema = useMemo(() => z.object({
    chosenDepartureDateType: z.enum(CHOSEN_DEPARTURE_DATE),
    datepicker: z.object({
      start: DateValue,
      end: DateValue
    }).nullable().refine(value => {
      if (!value?.start || !value?.end) return false;
      const nights_0 = value.end.compare(value.start);
      return nights_0 >= MIN_NIGHTS;
    }, {
      message: t('content.feedback.trip_error_min_nights', {
        minNights: MIN_NIGHTS
      })
    })
  }), [t]);
  const nightsSchema = useMemo(() => z.object({
    chosenDepartureDateType: z.enum(CHOSEN_DEPARTURE_DATE),
    nights: z.number().nullable().refine(value_0 => {
      if (!value_0) return false;
      return value_0 >= MIN_NIGHTS;
    }, {
      message: t('content.feedback.trip_error_min_nights', {
        minNights: MIN_NIGHTS
      })
    })
  }), [t]);
  type FlexibleDatesSchemaType = z.infer<typeof calendarSchema> & z.infer<typeof nightsSchema>;
  const methods = useForm<FlexibleDatesSchemaType>({
    resolver: zodResolver(datesTab === 'calendar' ? calendarSchema : nightsSchema),
    mode: 'onBlur',
    shouldFocusError: false,
    defaultValues: {
      chosenDepartureDateType: isPlaygroundBuildPage ? contextParams?.chosenDepartureDateType ?? 'EXACT' : preferences?.chosenDepartureDateType ?? 'EXACT',
      datepicker: defaultDatepickerValues,
      nights: defaultNightsValue
    }
  });
  const {
    formState: {
      errors
    },
    handleSubmit,
    control,
    setValue,
    clearErrors,
    watch
  } = methods;
  const currentNights = watch('nights');
  const checkDurationWarning = (newDuration: number | {
    start: DateValue;
    end: DateValue;
  }) => {
    const DURATION_WARNING_MESSAGES = [t('content.forms.duration_warning_reduced'), t('content.forms.duration_warning')];
    const contextStartDate = playgroundContext.chosenDepartureDate;
    const contextEndDate = playgroundContext.chosenReturnDate;
    const hasSelectedPlaces = playgroundContext.selectedDestinations.places.length > 0;
    if (!contextStartDate || !contextEndDate || !hasSelectedPlaces) return null;
    const contextStart = parseDate(contextStartDate);
    const contextEnd = parseDate(contextEndDate);
    const contextDuration = contextEnd.compare(contextStart);
    let newDurationNights: number;
    if (typeof newDuration === 'number') {
      newDurationNights = newDuration;
    } else {
      newDurationNights = newDuration.end.compare(newDuration.start);
    }
    if (newDurationNights < contextDuration) return DURATION_WARNING_MESSAGES;
    return null;
  };
  const handleDatesUpdate = async (data: FlexibleDatesSchemaType) => {
    const startDate = data.datepicker?.start?.toString().split('T')[0] ?? null;
    const endDate = data.datepicker?.end?.toString().split('T')[0] ?? null;
    const nights_1 = data.nights; // Already a number from our schema and form handling
    const trackingCategory = getTrackingCategory(pathname);

    // Track the event based on which tab was used
    if (datesTab === 'calendar' && startDate && endDate) {
      trackEvent(`dates_chosen`, {
        event_category: trackingCategory,
        event_label: `${startDate} to ${endDate}`,
        start_date: startDate,
        end_date: endDate
      });
    } else if (datesTab === 'nights' && nights_1) {
      trackEvent(`duration_chosen`, {
        event_category: trackingCategory,
        event_label: `${nights_1} nights`,
        duration: nights_1
      });
    }

    // only manipulate playground state if not on map/build page. In this case, we don't do our calls, we only change state. Calls are triggered by start form not this component.
    if (!isPlaygroundBuildPage) {
      setStartDate(startDate);
      setEndDate(endDate);
      setNights(nights_1);
      closeDrawer();
      return;
    }

    // otherwise we're on map/build
    try {
      let requestBody;
      if (datesTab === 'calendar' && startDate && endDate) {
        requestBody = {
          playgroundInputContext: {
            ...playgroundContext
          },
          chosenDepartureDate: startDate,
          chosenReturnDate: endDate
        };
      }
      if (datesTab === 'nights' && nights_1) {
        const contextStartDate_0 = getLowSeasonDefaultStartDate().toISOString().split('T')[0];
        const endDate_0 = parseDate(contextStartDate_0).add({
          days: nights_1
        }).toString().split('T')[0];
        requestBody = {
          playgroundInputContext: {
            ...playgroundContext
          },
          chosenDepartureDate: contextStartDate_0,
          chosenReturnDate: endDate_0
        };
      }
      if (!requestBody) return;
      const res = await addDepartureAndReturnDates.mutateAsync(requestBody);
      setContext(res as PlaygroundContext);
      setNights(nights_1);
      toasts.add({
        title: t('content.feedback.dates_filtered_title'),
        description: t('content.feedback.dates_filtered_description'),
        variant: 'success',
        position: 'bottom'
      }, {
        timeout: 5000
      });

      // Use window.location.search to get current URL state to preserve all params
      const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      if (datesTab === 'calendar' && startDate && endDate) {
        params.set('startDate', startDate);
        params.set('endDate', endDate);
        params.delete('nights');
      } else if (datesTab === 'nights' && nights_1) {
        params.set('nights', String(nights_1));
        params.delete('startDate');
        params.delete('endDate');
      }
      const query = params.toString();
      const nextUrl = query ? `${pathname}?${query}` : pathname || '/map/build';
      if (typeof window !== 'undefined') {
        window.history.replaceState(window.history.state, '', nextUrl);
      }
    } catch (error) {
      logError(error);
    } finally {
      closeDrawer();
    }
  };
  if (!contextParams) {
    return <div className="relative flex-grow">
        <Heading variant="h3">{t('common.loading')}</Heading>
      </div>;
  }
  return <div data-test-id="FlexibleDates">
      <form noValidate onSubmit={e => {
      e.preventDefault();
    }}>
        <div className={styles.tabbedContent}>
          <TabsContainer selectedKey={datesTab ?? 'nights'} onSelectionChange={key => {
          setDatesTab(key as 'calendar' | 'nights');
          clearErrors();
        }}>
            <div className={styles.tabsList}>
              <TabsList ariaLabel={t('aria.flexible_type')}>
                <Tab id="nights">{t('forms.flexible_dates')}</Tab>
                <Tab id="calendar">{t('forms.specific_dates')}</Tab>
              </TabsList>
            </div>

            <TabContent id="calendar">
              <Controller control={control} name="datepicker" render={({
              field: {
                value: value_1
              }
            }) => <div className={styles.content}>
                    {calendarWarning ? <DurationWarning durationWarning={calendarWarning} /> : <p className={styles.body}>{t('content.forms.dates_range_help')}</p>}
                    <RangeCalendar aria-label="Trip dates" value={value_1 || null} onChange={value_2 => {
                if (!value_2) return;
                clearErrors('datepicker');
                setValue('datepicker', value_2);
                setIsCalendarTouched(true);
                if (!isPlaygroundBuildPage) return;
                const newWarning = checkDurationWarning(value_2);
                setCalendarWarning(newWarning);
              }} minDuration={minimumDuration ?? MIN_NIGHTS} layout={layout} />
                  </div>} />
            </TabContent>

            <TabContent id="nights">
              <Controller control={control} name="nights" render={({
              field
            }) => <div className={styles.content}>
                    <p className={styles.body}>{t('content.forms.dates_flexible_help')}</p>
                    <DurationWarning durationWarning={nightsWarning} />
                    <Slider label={t('content.forms.nights_label')} renderOutput={v => t('content.forms.nights_count', {
                count: Array.isArray(v) ? v[0] : v
              })} defaultValue={9} maxValue={30} minValue={minimumDuration ?? MIN_NIGHTS} value={field.value ?? 9} onChange={value_3 => {
                field.onChange(value_3 ? Number(value_3) : null);
                if (!isPlaygroundBuildPage) return;
                const newWarning_0 = value_3 ? checkDurationWarning(Number(value_3)) : null;
                setNightsWarning(newWarning_0);
              }} />
                  </div>} />
            </TabContent>
          </TabsContainer>
        </div>

        <div className={clsx(styles.errorsContainer, styles.desktopOnly)}>
          {errors.datepicker && <p className={styles.error}>
              <WarningCircleSolid decorative />
              {errors.datepicker.message}
            </p>}
          {addDepartureAndReturnDates.error && <p className={styles.error}>
              <WarningCircleSolid decorative />
              {t('content.feedback.dates_update_error')}
            </p>}
        </div>
        <div className={styles.footer}>
          {(errors.datepicker || addDepartureAndReturnDates.error) && <div className={clsx(styles.errorsContainer, styles.mobileOnly)}>
              {errors.datepicker && <p className={styles.error}>
                  <WarningCircleSolid decorative />
                  {errors.datepicker.message}
                </p>}
              {addDepartureAndReturnDates.error && <p className={styles.error}>
                  <WarningCircleSolid decorative />
                  {t('content.feedback.dates_update_error')}
                </p>}
            </div>}
          <div className={styles.buttonContainer}>
            <p className={styles.nightsWarning}>
              <Lock strokeWidth={2.5} size="small" decorative />
              <span>
                {minimumDuration ? t('content.forms.nights_min_place', {
                minNights: String(minimumDuration)
              }) : t('content.forms.nights_min')}
              </span>
            </p>
            <Button isLoading={isLoading} isDisabled={isLoading || Object.keys(errors).length > 0 || !isCalendarTouched && datesTab === 'calendar' || datesTab === 'nights' && !!preferences?.nights && currentNights === nights} onPress={() => handleSubmit(handleDatesUpdate)()} size="small" noWrap>
              {datesTab === 'calendar' ? t('forms.confirm_dates') : t('forms.confirm_duration')}
            </Button>
          </div>
          {/* Note - logic above displaying close twice is silly but avoids dynamic resizing issue on ipad / iphone */}
        </div>
      </form>
    </div>;
};
function DurationWarning(t0) {
  const $ = _c(9);
  const {
    durationWarning
  } = t0;
  if (!durationWarning) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <WarningSolid decorative={true} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== durationWarning[0]) {
    t2 = <p className={styles.warning}>{t1}{durationWarning[0]}</p>;
    $[1] = durationWarning[0];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = clsx(styles.warning, styles.warningSubtitle);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== durationWarning[1]) {
    t4 = <p className={t3}>{durationWarning[1]}</p>;
    $[4] = durationWarning[1];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <Stack space="--by-space-200">{t2}{t4}</Stack>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export { FlexibleDatesPanel };